import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import {
  Briefcase,
  Buildings,
  Camera,
  Check,
  CreditCard,
  FileText,
  GearSix,
  PencilSimple,
  Plus,
  Sparkle,
  TrashSimple,
  UserPlus,
  UsersThree,
  UserSwitch,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router";

import { ThemeSwitch } from "@/client/components/theme-switch";
import { toast } from "@/client/hooks/use-toast";
import { queryClient } from "@/client/libs/query-client";
import type { Organization, OrgMember } from "@/client/services/organizations";
import {
  useAddMember,
  useBuyTokens,
  useCohort,
  useCreateOrganization,
  useMemberAnalyses,
  useMemberApplications,
  useMemberResumes,
  useMyOrganizations,
  useNotifyMemberManaged,
  useOrgBillingPortal,
  useOrgCheckout,
  useOrgMembers,
  useOrgPool,
  useRemoveMember,
  useUpdateOrganization,
} from "@/client/services/organizations";
import { useUploadImage } from "@/client/services/storage/upload-image";
import { useAuthStore } from "@/client/stores/auth";
import { useImpersonationStore } from "@/client/stores/impersonation";

import { LoadingPanel } from "../_components/loading-panel";
import { NotificationsBell } from "../_components/notifications";
import { ResumePreviewFrame } from "../resumes/_layouts/grid/_components/resume-preview-frame";

const initials = (name: string) =>
  name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ name, picture }: { name: string; picture?: string | null }) =>
  picture ? (
    <img src={picture} alt={name} className="size-9 shrink-0 rounded-full object-cover" />
  ) : (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
      {initials(name)}
    </div>
  );

const OrgLogo = ({
  org,
  canManage,
  size = "lg",
}: {
  org: Organization;
  canManage: boolean;
  size?: "sm" | "lg";
}) => {
  const { uploadImage } = useUploadImage();
  const { update } = useUpdateOrganization();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const box = size === "lg" ? "size-16 rounded-2xl" : "size-9 rounded-lg";
  const icon = size === "lg" ? "size-7" : "size-4";

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const response = await uploadImage(file);
      await update({ organizationId: org.id, logo: response.data });
    } catch (error) {
      toast({ variant: "error", title: t`Upload failed`, description: (error as Error).message });
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  return (
    <div className="relative shrink-0">
      <div
        className={cn("flex items-center justify-center overflow-hidden border bg-secondary", box)}
      >
        {org.logo ? (
          <img src={org.logo} alt={org.name} className="size-full object-cover" />
        ) : (
          <Buildings className={cn("opacity-40", icon)} />
        )}
      </div>
      {canManage && size === "lg" && (
        <>
          <button
            type="button"
            disabled={busy}
            className="absolute -bottom-1 -right-1 rounded-full border bg-background p-1.5 shadow transition-colors hover:bg-secondary"
            onClick={() => inputRef.current?.click()}
          >
            <Camera className="size-3.5" />
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        </>
      )}
    </div>
  );
};

const APPLICATION_STATUS: Record<string, string> = {
  to_apply: t`To apply`,
  applied: t`Applied`,
  interview: t`Interview`,
  offer: t`Offer`,
  rejected: t`Rejected`,
};

const SHORTLIST_TONE: Record<string, string> = {
  yes: "text-success",
  maybe: "text-warning",
  no: "text-error",
};

// Live grid of the member's resumes: click any card to open it in the builder
// and edit it as the org admin.
const MemberResumesTab = ({ org, member }: { org: Organization; member: OrgMember }) => {
  const { resumes, loading } = useMemberResumes(org.id, member.user.id, true);

  if (loading) return <p className="px-1 py-1.5 text-xs opacity-60">{t`Loading resumes...`}</p>;
  if ((resumes ?? []).length === 0)
    return <p className="px-1 py-1.5 text-xs opacity-60">{t`No resume to edit yet.`}</p>;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {(resumes ?? []).map((resume) => (
        <Link key={resume.id} to={`/builder/${resume.id}`} className="group block">
          <div className="aspect-[1/1.4142] overflow-hidden rounded-lg border bg-white ring-primary transition-all group-hover:ring-2">
            <ResumePreviewFrame resume={resume} />
          </div>
          <div className="mt-1.5 flex items-center gap-1">
            <span className="min-w-0 flex-1 truncate text-xs font-medium">{resume.title}</span>
            <PencilSimple className="size-3 shrink-0 opacity-50 transition-opacity group-hover:opacity-100" />
          </div>
        </Link>
      ))}
    </div>
  );
};

// The member's tracked applications, so the admin follows their job search.
const MemberApplicationsTab = ({ org, member }: { org: Organization; member: OrgMember }) => {
  const { applications, loading } = useMemberApplications(org.id, member.user.id, true);

  if (loading)
    return <p className="px-1 py-1.5 text-xs opacity-60">{t`Loading applications...`}</p>;
  if ((applications ?? []).length === 0)
    return <p className="px-1 py-1.5 text-xs opacity-60">{t`No application tracked yet.`}</p>;

  return (
    <div className="divide-y rounded-lg border bg-background">
      {(applications ?? []).map((app) => (
        <div key={app.id} className="flex items-center gap-x-3 p-2.5">
          <Briefcase className="size-4 shrink-0 opacity-40" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{app.jobTitle || t`Untitled role`}</p>
            <p className="truncate text-xs opacity-60">{app.company || t`Unknown company`}</p>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {APPLICATION_STATUS[app.status] ?? app.status}
          </Badge>
          {app.url && (
            <a
              href={app.url}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 text-xs underline opacity-60 hover:opacity-100"
            >
              {t`Link`}
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

// The member's saved AI analyses (ATS score + recruiter shortlist verdict).
const MemberAnalysesTab = ({ org, member }: { org: Organization; member: OrgMember }) => {
  const { analyses, loading } = useMemberAnalyses(org.id, member.user.id, true);

  if (loading) return <p className="px-1 py-1.5 text-xs opacity-60">{t`Loading analyses...`}</p>;
  if ((analyses ?? []).length === 0)
    return <p className="px-1 py-1.5 text-xs opacity-60">{t`No analysis run yet.`}</p>;

  return (
    <div className="divide-y rounded-lg border bg-background">
      {(analyses ?? []).map((analysis) => (
        <div key={analysis.id} className="flex items-center gap-x-3 p-2.5">
          <Sparkle className="size-4 shrink-0 opacity-40" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{analysis.jobTitle || t`Job analysis`}</p>
            <p className="truncate text-xs opacity-60">{analysis.resumeTitle}</p>
          </div>
          <span
            className={cn(
              "shrink-0 text-xs font-semibold capitalize",
              SHORTLIST_TONE[analysis.shortlist] ?? "opacity-60",
            )}
          >
            {analysis.shortlist}
          </span>
          <Badge variant="secondary" className="shrink-0 tabular-nums">
            {analysis.atsScore}/100
          </Badge>
        </div>
      ))}
    </div>
  );
};

const MEMBER_TABS = [
  { key: "resumes" as const, label: t`Resumes`, icon: FileText },
  { key: "applications" as const, label: t`Applications`, icon: Briefcase },
  { key: "analyses" as const, label: t`Analyses`, icon: Sparkle },
];

const MemberRow = ({ org, member }: { org: Organization; member: OrgMember }) => {
  const { removeMember } = useRemoveMember();
  const { notifyManaged } = useNotifyMemberManaged();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"resumes" | "applications" | "analyses">("resumes");

  const navigate = useNavigate();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const setImpersonatedMember = useImpersonationStore((state) => state.setMember);
  const isSelf = currentUserId === member.user.id;

  // Enter "act as member" mode: every data request now targets this member's
  // account (resumes, jobs, applications, AI), so the admin can view, edit and
  // even apply on their behalf. A banner in the layout offers a way back out.
  const onManageAccount = () => {
    // Transparency: let the member know their account is being managed.
    // Best-effort, so a mail hiccup never blocks entering the mode.
    notifyManaged({ organizationId: org.id, userId: member.user.id }).catch(() => {
      // ignore: notification is non-critical
    });

    setImpersonatedMember({
      id: member.user.id,
      name: member.user.name,
      email: member.user.email,
      picture: member.user.picture,
    });
    // Drop the admin's cached data so the member's data is fetched fresh
    // (queries are configured with refetchOnMount: false, so invalidating is
    // not enough here).
    queryClient.removeQueries();
    void navigate("/dashboard/resumes");
  };

  return (
    <div>
      <div className="flex items-center gap-x-2 p-3">
        <Avatar name={member.user.name} picture={member.user.picture} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{member.user.name}</p>
          <p className="truncate text-xs opacity-60">{member.user.email}</p>
        </div>
        {!isSelf && (
          <Button size="sm" className="gap-1.5" onClick={onManageAccount}>
            <UserSwitch className="size-4" />
            <span className="hidden sm:inline">{t`Manage account`}</span>
          </Button>
        )}
        <Button
          size="sm"
          variant={open ? "secondary" : "outline"}
          onClick={() => {
            setOpen((value) => !value);
          }}
        >
          <UsersThree className="mr-1.5 size-4" />
          {open ? t`Close` : t`Overview`}
        </Button>
        {member.role === "admin" ? (
          <Badge variant="secondary">{t`Admin`}</Badge>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            className="text-error"
            onClick={() => void removeMember({ organizationId: org.id, userId: member.user.id })}
          >
            <TrashSimple />
          </Button>
        )}
      </div>

      {open && (
        <div className="border-t bg-secondary-accent/20 p-3">
          <div className="mb-3 flex gap-1">
            {MEMBER_TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={cn(
                  "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                  tab === key ? "bg-primary text-primary-foreground" : "hover:bg-secondary",
                )}
                onClick={() => {
                  setTab(key);
                }}
              >
                <Icon className="size-3.5" />
                {label}
              </button>
            ))}
          </div>

          {tab === "resumes" && <MemberResumesTab org={org} member={member} />}
          {tab === "applications" && <MemberApplicationsTab org={org} member={member} />}
          {tab === "analyses" && <MemberAnalysesTab org={org} member={member} />}
        </div>
      )}
    </div>
  );
};

const MembersPanel = ({ org }: { org: Organization }) => {
  const { members } = useOrgMembers(org.id);
  const { addMember, loading } = useAddMember();
  const [email, setEmail] = useState("");

  const onAdd = async () => {
    if (!email.trim()) return;
    try {
      await addMember({ organizationId: org.id, email: email.trim() });
      setEmail("");
      toast({ variant: "success", title: t`Member added.` });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Could not add member`,
        description: (error as Error).message,
      });
    }
  };

  return (
    <div className="rounded-2xl border">
      <div className="flex items-center gap-x-2 border-b p-4">
        <UsersThree className="text-primary" />
        <h3 className="font-semibold">{t`Members`}</h3>
        <Badge variant="secondary" className="ml-auto">
          {t`${org.seats} seats`}
        </Badge>
      </div>

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-end gap-2">
          <Input
            value={email}
            type="email"
            placeholder={t`member@email.com`}
            className="w-full flex-1 sm:w-auto"
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            onKeyDown={(event) => event.key === "Enter" && onAdd()}
          />
          <Button disabled={loading || !email.trim()} onClick={onAdd}>
            <UserPlus className="mr-2" />
            {t`Add seat`}
          </Button>
        </div>

        <div className="divide-y rounded-xl border">
          {(members ?? []).map((member) => (
            <MemberRow key={member.id} org={org} member={member} />
          ))}
          {members?.length === 0 && (
            <p className="p-4 text-center text-sm opacity-60">
              {t`No members yet. Add seats by email above.`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const SettingsPanel = ({ org }: { org: Organization }) => {
  const { update, loading } = useUpdateOrganization();
  const [name, setName] = useState(org.name);
  const [plan, setPlan] = useState(org.plan);

  const dirty = name.trim() !== org.name || plan !== org.plan;

  const onSave = async () => {
    if (!dirty || !name.trim()) return;
    try {
      await update({ organizationId: org.id, name: name.trim(), plan });
      toast({ variant: "success", title: t`Saved.` });
    } catch (error) {
      toast({ variant: "error", title: t`Could not save`, description: (error as Error).message });
    }
  };

  return (
    <div className="rounded-2xl border">
      <div className="border-b p-4">
        <h3 className="font-semibold">{t`Settings`}</h3>
      </div>
      <div className="grid gap-4 p-4 sm:grid-cols-2">
        <div className="grid gap-1">
          <label className="text-xs opacity-60">{t`Organization name`}</label>
          <Input
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>
        <div className="grid gap-1">
          <label className="text-xs opacity-60">{t`Members' plan`}</label>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pro">{t`Pro`}</SelectItem>
              <SelectItem value="autopilot">{t`Autopilot`}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Button disabled={!dirty || loading} onClick={onSave}>
            <Check className="mr-2" />
            {t`Save changes`}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="rounded-2xl border p-4">
    <p className="text-2xl font-bold tabular-nums">{value}</p>
    <p className="mt-0.5 text-xs opacity-60">{label}</p>
  </div>
);

const formatTokens = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1000
      ? `${Math.round(n / 1000)}k`
      : `${n}`;

const CohortPanel = ({ org }: { org: Organization }) => {
  const { cohort } = useCohort(org.id);
  if (!cohort) return null;

  const { totals, members } = cohort;

  return (
    <div className="rounded-2xl border">
      <div className="flex items-center gap-x-2 border-b p-4">
        <UsersThree className="text-primary" />
        <h3 className="font-semibold">{t`Cohort progress`}</h3>
      </div>

      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
        <Stat label={t`Members`} value={totals.members} />
        <Stat label={t`Applications`} value={totals.sent} />
        <Stat label={t`Callback rate`} value={`${totals.callbackRate}%`} />
        <Stat label={t`Offers`} value={totals.offers} />
      </div>

      {/* Organization AI consumption (shared visibility for the admin) */}
      <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
        <div>
          <p className="text-sm font-medium">{t`AI consumption this month`}</p>
          <p className="text-xs opacity-60">{t`Tokens used across all members`}</p>
        </div>
        <p className="text-lg font-bold tabular-nums">{formatTokens(totals.tokensUsed)}</p>
      </div>

      <div className="divide-y border-t">
        {members.map((member) => (
          <div key={member.user.id} className="flex items-center gap-x-3 p-3">
            <Avatar name={member.user.name} picture={member.user.picture} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{member.user.name}</p>
              <p className="text-xs opacity-60">
                {t`${member.resumes} resumes`} · {t`${member.sent} sent`} ·{" "}
                {t`${formatTokens(member.tokensUsed)} tokens`}
              </p>
            </div>
            <div className="flex w-28 shrink-0 items-center gap-x-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                <div
                  className={cn(
                    "h-full rounded-full",
                    member.callbackRate >= 30 ? "bg-success" : "bg-primary",
                  )}
                  style={{ width: `${member.callbackRate}%` }}
                />
              </div>
              <span className="w-9 text-right text-xs font-semibold tabular-nums">
                {member.callbackRate}%
              </span>
            </div>
          </div>
        ))}
        {members.length === 0 && (
          <p className="p-4 text-center text-sm opacity-60">{t`No members to track yet.`}</p>
        )}
      </div>
    </div>
  );
};

// Per-seat subscription for the organization: activate billing or manage the
// existing subscription (seats, cancellation) through the Stripe portal.
const BillingPanel = ({ org }: { org: Organization }) => {
  const { pool } = useOrgPool(org.id);
  const { checkout, loading: checkingOut } = useOrgCheckout();
  const { openPortal, loading: opening } = useOrgBillingPortal();

  // Only relevant when Stripe is configured on the instance.
  if (!pool?.billingEnabled) return null;

  const active = org.subscriptionStatus === "active" || org.subscriptionStatus === "trialing";
  const renew = org.currentPeriodEnd ? new Date(org.currentPeriodEnd).toLocaleDateString() : null;

  const onSubscribe = async () => {
    try {
      await checkout({ organizationId: org.id, plan: org.plan as "pro" | "autopilot" });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Could not start checkout`,
        description: (error as Error).message,
      });
    }
  };

  const onManage = async () => {
    try {
      await openPortal(org.id);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Could not open billing`,
        description: (error as Error).message,
      });
    }
  };

  return (
    <div className="rounded-2xl border">
      <div className="flex items-center gap-x-2 border-b p-4">
        <CreditCard className="text-primary" />
        <h3 className="font-semibold">{t`Billing`}</h3>
        {active && (
          <Badge variant="success" className="ml-auto capitalize">
            {org.subscriptionStatus}
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          {active ? (
            <>
              <p className="text-sm font-medium">{t`${org.seats} seats on the ${org.plan} plan`}</p>
              {renew && <p className="text-xs opacity-60">{t`Renews on ${renew}`}</p>}
            </>
          ) : (
            <>
              <p className="text-sm font-medium">{t`No active subscription`}</p>
              <p className="text-xs opacity-60">
                {t`Activate per-seat billing so members keep their plan.`}
              </p>
            </>
          )}
        </div>

        {active ? (
          <Button variant="outline" disabled={opening} onClick={onManage}>
            {t`Manage billing`}
          </Button>
        ) : (
          <Button disabled={checkingOut} onClick={onSubscribe}>
            <CreditCard className="mr-2 size-4" />
            {t`Activate billing`}
          </Button>
        )}
      </div>
    </div>
  );
};

// Shared AI token pool: monthly fair-use allowance + purchased top-up balance,
// with a one-click Stripe checkout to buy more when it runs low.
const PoolPanel = ({ org }: { org: Organization }) => {
  const { pool } = useOrgPool(org.id);
  const { buyTokens, loading } = useBuyTokens();
  if (!pool) return null;

  const usedPct = pool.allowance > 0 ? Math.min(100, (pool.used / pool.allowance) * 100) : 0;
  const low = pool.remaining < pool.allowance * 0.15;

  const onBuy = async () => {
    try {
      const { url } = await buyTokens({ organizationId: org.id, quantity: 1 });
      window.location.href = url;
    } catch (error) {
      toast({
        variant: "error",
        title: t`Could not start checkout`,
        description: (error as Error).message,
      });
    }
  };

  return (
    <div className="rounded-2xl border">
      <div className="flex items-center gap-x-2 border-b p-4">
        <Sparkle className="text-primary" />
        <h3 className="font-semibold">{t`AI token pool`}</h3>
        {pool.billingEnabled && (
          <Button
            size="sm"
            variant={low ? "primary" : "outline"}
            className="ml-auto"
            disabled={loading}
            onClick={onBuy}
          >
            <Plus className="mr-1.5 size-4" />
            {t`Buy tokens`}
          </Button>
        )}
      </div>

      <div className="space-y-4 p-4">
        <div>
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="opacity-70">{t`Monthly allowance`}</span>
            <span className="font-medium tabular-nums">
              {formatTokens(pool.used)} / {pool.allowance > 0 ? formatTokens(pool.allowance) : "∞"}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary">
            <div
              className={cn("h-full rounded-full", usedPct >= 85 ? "bg-error" : "bg-primary")}
              style={{ width: `${usedPct}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border p-3">
            <p className="text-lg font-bold tabular-nums">{formatTokens(pool.balance)}</p>
            <p className="text-xs opacity-60">{t`Purchased balance`}</p>
          </div>
          <div className="rounded-xl border p-3">
            <p className="text-lg font-bold tabular-nums">{formatTokens(pool.remaining)}</p>
            <p className="text-xs opacity-60">{t`Remaining this month`}</p>
          </div>
        </div>

        <p className="text-xs opacity-60">
          {pool.billingEnabled
            ? t`Members draw from this shared pool. Overage uses the purchased balance, which never expires.`
            : t`Billing is off, so AI usage is unlimited on this instance.`}
        </p>
      </div>
    </div>
  );
};

const OrgDashboard = ({ org }: { org: Organization }) => {
  const canManage = org.role === "admin";
  const [tab, setTab] = useState<"cohort" | "billing" | "settings">("cohort");

  const active = org.subscriptionStatus === "active" || org.subscriptionStatus === "trialing";

  const tabs = [
    { key: "cohort" as const, label: t`Cohort & members`, icon: UsersThree },
    { key: "billing" as const, label: t`Billing`, icon: CreditCard },
    { key: "settings" as const, label: t`Settings`, icon: GearSix },
  ];

  return (
    <div className="space-y-5">
      {/* Identity header, kept separate from the operational content below. */}
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border p-5">
        <OrgLogo org={org} canManage={canManage} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-2xl font-bold">{org.name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm opacity-70">
            <Badge variant="primary" className="capitalize">
              {org.plan}
            </Badge>
            {active ? (
              <Badge variant="success">{t`Billing active`}</Badge>
            ) : (
              <span className="capitalize">
                {org.billingModel === "cohort" ? t`Cohort billing` : t`Per seat`}
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold tabular-nums">
            {org.seatLimit ? `${org.seats} / ${org.seatLimit}` : org.seats}
          </p>
          <p className="text-xs opacity-60">{t`Seats`}</p>
        </div>
      </div>

      {canManage ? (
        <>
          {/* One clear place to switch between the operational, billing and
              configuration areas, instead of one long mixed column. */}
          <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-full border bg-secondary-accent/20 p-1">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={cn(
                  "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors",
                  tab === key
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "hover:bg-background",
                )}
                onClick={() => {
                  setTab(key);
                }}
              >
                <Icon className="size-4" />
                {label}
              </button>
            ))}
          </div>

          {tab === "cohort" && (
            <div className="space-y-5">
              <CohortPanel org={org} />
              <MembersPanel org={org} />
            </div>
          )}
          {tab === "billing" && (
            <div className="space-y-5">
              <BillingPanel org={org} />
              <PoolPanel org={org} />
            </div>
          )}
          {tab === "settings" && <SettingsPanel org={org} />}
        </>
      ) : (
        <div className="rounded-2xl border p-6 text-sm opacity-60">
          {t`You are a member of this organization. Only admins manage seats and settings.`}
        </div>
      )}
    </div>
  );
};

export const OrganizationsPage = () => {
  const { organizations, loading } = useMyOrganizations();
  const { create, loading: creating } = useCreateOrganization();

  const [name, setName] = useState("");
  const [plan, setPlan] = useState("pro");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const hasOrgs = (organizations?.length ?? 0) > 0;
  const selected = organizations?.find((org) => org.id === selectedId) ?? organizations?.[0];

  const onCreate = async () => {
    if (!name.trim()) return;
    try {
      const org = await create({ name: name.trim(), plan });
      setName("");
      setSelectedId(org.id);
      toast({ variant: "success", title: t`Organization created.` });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Could not create`,
        description: (error as Error).message,
      });
    }
  };

  const steps = [
    { n: 1, title: t`Create an organization`, text: t`Pick the plan your members should get.` },
    { n: 2, title: t`Invite by email`, text: t`Add each member's email as a seat.` },
    { n: 3, title: t`They inherit your plan`, text: t`Members instantly get Pro or Autopilot.` },
  ];

  return (
    <>
      <Helmet>
        <title>
          {t`Organizations`} - {t`Izicvs`}
        </title>
      </Helmet>

      <div className="space-y-6">
        {/* Header: title + intro, with the shared notification + theme cluster. */}
        <div className="flex items-start justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-1"
          >
            <h1 className="flex items-center gap-x-2 text-3xl font-bold tracking-tight lg:text-4xl">
              <Buildings weight="fill" className="text-primary" />
              {t`Organizations`}
            </h1>
            <p className="max-w-2xl text-sm opacity-75">
              {t`For coaches, bootcamps and universities: equip your people and let them inherit your plan.`}
            </p>
          </motion.div>

          <div className="flex shrink-0 items-center gap-x-3">
            <NotificationsBell />
            <ThemeSwitch />
          </div>
        </div>

        {loading && <LoadingPanel />}

        {!loading && !hasOrgs && (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-3">
              {steps.map((step) => (
                <div key={step.n} className="rounded-2xl border p-4">
                  <div className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {step.n}
                  </div>
                  <p className="mt-2 text-sm font-semibold">{step.title}</p>
                  <p className="text-xs opacity-60">{step.text}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-dashed p-8 text-center">
              <Buildings className="mx-auto size-8 opacity-30" />
              <p className="mt-2 font-medium">{t`Create your first organization`}</p>
              <div className="mx-auto mt-4 flex max-w-md flex-wrap items-end justify-center gap-2">
                <Input
                  value={name}
                  placeholder={t`e.g. Acme Bootcamp, Cohort 12`}
                  className="w-full sm:w-64"
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                  onKeyDown={(event) => event.key === "Enter" && onCreate()}
                />
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger className="w-full sm:w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pro">{t`Pro`}</SelectItem>
                    <SelectItem value="autopilot">{t`Autopilot`}</SelectItem>
                  </SelectContent>
                </Select>
                <Button disabled={creating || !name.trim()} onClick={onCreate}>
                  <Plus className="mr-2" />
                  {t`Create`}
                </Button>
              </div>
            </div>
          </div>
        )}

        {!loading && hasOrgs && (
          <div className="grid gap-6 lg:grid-cols-[17rem_1fr]">
            {/* Left rail: switch between organizations, and create a new one.
                Kept distinct from the selected org's management area. */}
            <aside className="space-y-4">
              <div className="rounded-2xl border">
                <p className="border-b px-4 py-3 text-xs font-semibold uppercase tracking-wide opacity-60">
                  {t`Your organizations`}
                </p>
                <div className="space-y-1 p-2">
                  {(organizations ?? []).map((org) => (
                    <button
                      key={org.id}
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-x-3 rounded-xl p-2.5 text-left transition-colors",
                        selected?.id === org.id
                          ? "bg-primary/10 ring-1 ring-primary"
                          : "hover:bg-secondary-accent/20",
                      )}
                      onClick={() => {
                        setSelectedId(org.id);
                      }}
                    >
                      <OrgLogo org={org} canManage={false} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{org.name}</p>
                        <p className="truncate text-xs capitalize opacity-60">
                          {t`${org.seats} seats`} · {org.plan}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-dashed p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-60">
                  {t`New organization`}
                </p>
                <div className="space-y-2">
                  <Input
                    value={name}
                    placeholder={t`Organization name`}
                    className="w-full"
                    onChange={(event) => {
                      setName(event.target.value);
                    }}
                    onKeyDown={(event) => event.key === "Enter" && onCreate()}
                  />
                  <div className="flex gap-2">
                    <Select value={plan} onValueChange={setPlan}>
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pro">{t`Pro`}</SelectItem>
                        <SelectItem value="autopilot">{t`Autopilot`}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="icon" disabled={creating || !name.trim()} onClick={onCreate}>
                      <Plus />
                    </Button>
                  </div>
                </div>
              </div>
            </aside>

            <main>{selected && <OrgDashboard org={selected} />}</main>
          </div>
        )}
      </div>
    </>
  );
};
